// Live updates: one EventSource on /api/events. Each event type invalidates the right queries.
// When the stream is down we poll every 15 s instead, so the screen never goes stale silently.

import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

export interface AppEvent {
  type: 'conversation' | 'health' | 'self_test' | 'scheduled' | string;
  id?: string;
  workspace_id?: string;
  assigned_user_id?: string | null;
}

type Listener = (e: AppEvent) => void;
const listeners = new Set<Listener>();

/** Subscribe to raw events (the hand-off toast uses this). Returns the unsubscribe. */
export function onAppEvent(fn: Listener): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

const POLL_MS = 15_000;

export function useSse(enabled: boolean): { connected: boolean } {
  const qc = useQueryClient();
  const [connected, setConnected] = useState(false);
  const pollRef = useRef<number | null>(null);

  useEffect(() => {
    if (!enabled) return;
    let es: EventSource | null = null;
    let closed = false;
    let retryTimer: number | null = null;

    const invalidateAll = () => {
      qc.invalidateQueries({ queryKey: ['conversations'] });
      qc.invalidateQueries({ queryKey: ['conversation'] });
      qc.invalidateQueries({ queryKey: ['counts'] });
      qc.invalidateQueries({ queryKey: ['mine-unread'] });
      qc.invalidateQueries({ queryKey: ['health'] });
      qc.invalidateQueries({ queryKey: ['lines'] });
      qc.invalidateQueries({ queryKey: ['self-test'] });
      qc.invalidateQueries({ queryKey: ['scheduled'] });
    };

    const startPolling = () => {
      if (pollRef.current != null) return;
      pollRef.current = window.setInterval(invalidateAll, POLL_MS);
    };
    const stopPolling = () => {
      if (pollRef.current != null) {
        window.clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };

    const handle = (e: AppEvent) => {
      switch (e.type) {
        case 'conversation':
          qc.invalidateQueries({ queryKey: ['conversations'] });
          qc.invalidateQueries({ queryKey: ['counts'] });
          qc.invalidateQueries({ queryKey: ['mine-unread'] });
          if (e.id) qc.invalidateQueries({ queryKey: ['conversation', e.id] });
          else qc.invalidateQueries({ queryKey: ['conversation'] });
          qc.invalidateQueries({ queryKey: ['contact'] });
          break;
        case 'health':
          qc.invalidateQueries({ queryKey: ['health'] });
          qc.invalidateQueries({ queryKey: ['lines'] });
          break;
        case 'self_test':
          qc.invalidateQueries({ queryKey: ['self-test'] });
          break;
        case 'scheduled':
          qc.invalidateQueries({ queryKey: ['scheduled'] });
          break;
        default:
          invalidateAll();
      }
      for (const fn of listeners) {
        try {
          fn(e);
        } catch {
          /* a listener must never break the stream */
        }
      }
    };

    const parse = (raw: string, fallbackType?: string): AppEvent | null => {
      try {
        const obj = JSON.parse(raw) as AppEvent;
        if (obj && typeof obj === 'object') {
          if (!obj.type && fallbackType) obj.type = fallbackType;
          return obj.type ? obj : null;
        }
      } catch {
        /* not JSON: ignore keepalives */
      }
      return fallbackType ? { type: fallbackType } : null;
    };

    const connect = () => {
      if (closed) return;
      es = new EventSource('/api/events', { withCredentials: true });
      es.onopen = () => {
        setConnected(true);
        stopPolling();
        // anything could have changed while we were away
        invalidateAll();
      };
      es.onmessage = (ev) => {
        const e = parse(ev.data);
        if (e) handle(e);
      };
      // the server may use named events instead of the default "message"
      for (const name of ['conversation', 'health', 'self_test', 'scheduled']) {
        es.addEventListener(name, (ev) => {
          const e = parse((ev as MessageEvent).data ?? '{}', name);
          if (e) handle(e);
        });
      }
      es.onerror = () => {
        setConnected(false);
        startPolling();
        if (es && es.readyState === EventSource.CLOSED) {
          es.close();
          es = null;
          if (retryTimer == null) {
            retryTimer = window.setTimeout(() => {
              retryTimer = null;
              connect();
            }, 5000);
          }
        }
      };
    };

    connect();
    return () => {
      closed = true;
      if (retryTimer != null) window.clearTimeout(retryTimer);
      stopPolling();
      es?.close();
    };
  }, [enabled, qc]);

  return { connected };
}
